// Partners — per-country governing entities (plan/partners.md). Sleek global-admin surface:
// create a partner (which auto-creates its house company + cap table and makes the owner govern
// every company in that country), and manage its members + shareholders.
//
// Inputs are kept in the STATIC template (never inside a ${() => …} reactive slot — the framework
// re-render footgun), and the country/owner/member pickers are searchSelect comboboxes built ONCE
// and moved in by node interpolation. Shareholders are added one row at a time from three static
// inputs into a signal array that renders as plain (non-input) rows, so there is no dynamic list of
// live inputs.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { focusModal } from '../shared/modal.js';
import { searchSelect } from '../shared/searchselect.js';

const MEMBER_ROLES = [
  { value: 'owner', label: 'Owner (super admin)' },
  { value: 'admin', label: 'Admin' },
  { value: 'support', label: 'Support (read-only)' },
];
const roleLabel = v => (MEMBER_ROLES.find(r => r.value === v) || {}).label || v || '';
const capTotal = rows => rows.reduce((s, r) => s + (Number(r.share_pct) || 0), 0);
const fmtPct = n => String(Number(n));   // 37.5 → "37.5", 25 → "25"

export function partnersScreen() {
  const rows = signal([]), loading = signal(true), loadErr = signal('');

  // ── create form ──
  const formOpen = signal(false), fName = signal(''), fCountry = signal('');
  const fOwner = signal(''), capRows = signal([]);          // [{holder_name, share_pct, is_sleek}]
  const hName = signal(''), hPct = signal(''), hSleek = signal(false);
  const formErr = signal(''), saving = signal(false);

  const countryPick = searchSelect({
    placeholder: '— Select a country —', ariaLabel: 'Country', className: 'form-control',
    onSelect: v => { fCountry.value = v || ''; },
  });
  const ownerPick = searchSelect({
    placeholder: '— Owner / super admin (optional) —', ariaLabel: 'Owner', className: 'form-control',
    onSelect: v => { fOwner.value = v || ''; },
  });

  // ── manage modal ──
  const manageFor = signal(null);            // the partner being managed
  const detail = signal(null);               // full partner (members + shareholders)
  const mLoading = signal(false), mErr = signal(''), mBusy = signal('');
  const mRole = signal('admin');
  const mCap = signal([]);                    // editable cap table in the manage modal
  const mName = signal(''), mPct = signal(''), mSleek = signal(false);
  const memberPick = searchSelect({
    placeholder: '— Add a member —', ariaLabel: 'Member', className: 'form-control',
    onSelect: () => {},                       // read on Add click
  });

  async function load() {
    loading.value = true; loadErr.value = '';
    try { const r = await api.get('/admin/partners'); rows.value = r.data || []; }
    catch (err) { rows.value = []; loadErr.value = (err.data && err.data.error) || err.message || 'Could not load partners'; }
    loading.value = false;
  }
  load();

  async function loadCountries() {
    try { const r = await api.get('/admin/countries?per_page=300');
      countryPick.setOptions((r.data || []).map(c => ({ value: String(c.id),
        label: c.name || c.iso2 || ('Country #' + c.id), keywords: c.iso2 || '' }))); } catch { /* leave empty */ }
  }
  async function loadUsers(pick) {
    try { const r = await api.get('/admin/users?per_page=300');
      pick.setOptions((r.data || []).map(u => ({ value: String(u.id),
        label: (u.display_name ? u.display_name + ' · ' : '') + (u.email || ('User #' + u.id)),
        keywords: u.email || '' }))); } catch { /* leave empty */ }
  }

  // ── create ──
  function openForm() {
    fName.value = ''; fCountry.value = ''; fOwner.value = ''; capRows.value = [];
    hName.value = ''; hPct.value = ''; hSleek.value = false; formErr.value = '';
    countryPick.set(''); ownerPick.set('');
    loadCountries(); loadUsers(ownerPick); formOpen.value = true; focusModal();
  }
  function closeForm() { formOpen.value = false; }
  function addHolder() {
    const name = hName.value.trim(); const pct = Number(hPct.value);
    if (!name || !(pct > 0)) { formErr.value = 'Enter a holder name and a positive %.'; return; }
    capRows.value = [...capRows.value, { holder_name: name, share_pct: pct, is_sleek: !!hSleek.value }];
    hName.value = ''; hPct.value = ''; hSleek.value = false; formErr.value = '';
  }
  function removeHolder(i) { capRows.value = capRows.value.filter((_, ix) => ix !== i); }

  async function save(e) {
    e.preventDefault();
    if (!fName.value.trim()) { formErr.value = 'Partner name is required.'; return; }
    if (!fCountry.value) { formErr.value = 'Pick a country.'; return; }
    if (capRows.value.length && capTotal(capRows.value) !== 100) {
      formErr.value = 'Shareholdings must sum to 100 (currently ' + capTotal(capRows.value) + ').'; return;
    }
    saving.value = true; formErr.value = '';
    try {
      await api.post('/admin/partners', {
        country_id: Number(fCountry.value), name: fName.value.trim(),
        owner_user_id: fOwner.value ? Number(fOwner.value) : null,
        shareholders: capRows.value.length ? capRows.value : null,
      });
      formOpen.value = false; await load();
    } catch (err) { formErr.value = (err.data && err.data.error) || err.message || 'Could not create partner'; }
    saving.value = false;
  }

  // ── manage ──
  async function openManage(p) {
    manageFor.value = p; detail.value = null; mErr.value = ''; mRole.value = 'admin';
    mName.value = ''; mPct.value = ''; mSleek.value = false; memberPick.set('');
    loadUsers(memberPick); await loadDetail(p.id); focusModal();
  }
  function closeManage() { manageFor.value = null; detail.value = null; }
  async function loadDetail(pid) {
    mLoading.value = true; mErr.value = '';
    try { const d = await api.get('/admin/partners/' + pid); detail.value = d;
      mCap.value = (d.shareholders || []).map(s => ({ holder_name: s.holder_name,
        share_pct: Number(s.share_pct), is_sleek: !!s.is_sleek })); }
    catch (err) { mErr.value = (err.data && err.data.error) || err.message || 'Could not load partner'; }
    mLoading.value = false;
  }
  async function addMember() {
    const p = manageFor.value; const uid = memberPick.get();
    if (!p || !uid) { mErr.value = 'Pick a user to add.'; return; }
    mBusy.value = 'member'; mErr.value = '';
    try { await api.post('/admin/partners/' + p.id + '/members', { user_id: Number(uid), role: mRole.value });
      memberPick.set(''); await loadDetail(p.id); }
    catch (err) { mErr.value = (err.data && err.data.error) || err.message || 'Could not add member'; }
    mBusy.value = '';
  }
  async function removeMember(uid) {
    const p = manageFor.value; if (!p) return;
    mBusy.value = 'm' + uid; mErr.value = '';
    try { await api.del('/admin/partners/' + p.id + '/members/' + uid); await loadDetail(p.id); }
    catch (err) { mErr.value = (err.data && err.data.error) || err.message || 'Could not remove member'; }
    mBusy.value = '';
  }
  function addCap() {
    const name = mName.value.trim(); const pct = Number(mPct.value);
    if (!name || !(pct > 0)) { mErr.value = 'Enter a holder name and a positive %.'; return; }
    mCap.value = [...mCap.value, { holder_name: name, share_pct: pct, is_sleek: !!mSleek.value }];
    mName.value = ''; mPct.value = ''; mSleek.value = false; mErr.value = '';
  }
  function removeCap(i) { mCap.value = mCap.value.filter((_, ix) => ix !== i); }
  async function saveCap() {
    const p = manageFor.value; if (!p) return;
    if (capTotal(mCap.value) !== 100) { mErr.value = 'Shareholdings must sum to 100 (currently ' + capTotal(mCap.value) + ').'; return; }
    mBusy.value = 'cap'; mErr.value = '';
    try { await api.patch('/admin/partners/' + p.id + '/shareholders', { shareholders: mCap.value });
      await loadDetail(p.id); }
    catch (err) { mErr.value = (err.data && err.data.error) || err.message || 'Could not save cap table'; }
    mBusy.value = '';
  }

  // ── render: ONE reactive block, all signals read at top ──
  const list = () => {
    if (loading.value) return html`<div class="muted-block">Loading…</div>`;
    if (loadErr.value) return html`<div class="alert alert-danger py-2">${loadErr.value}</div>`;
    if (!rows.value.length) return html`<div class="muted-block">No partners yet.</div>`;
    return html`<div class="row-list">${rows.value.map(p => html`
      <div class=${'row-card' + (p.is_active ? '' : ' inactive')}>
        <span class="row-main"><b>${p.name}</b>
          <span class="muted"> — ${p.country_name || '—'}</span>
          ${p.is_active ? '' : html`<span class="tag-off">inactive</span>`}</span>
        <span class="muted">${p.house_company_name ? 'books: ' + p.house_company_name : 'no house company'}</span>
        <span class="badge badge-light">${p.member_count || 0} member${(p.member_count === 1) ? '' : 's'}</span>
        <button type="button" class="btn btn-sm btn-outline" @click=${() => openManage(p)}>Manage</button>
      </div>`)}</div>`;
  };

  const capEditor = (list, onRemove) => html`<div class="row-list grants-list">
    ${list.map((r, i) => html`<div class="row-card">
      <span class="row-main"><b>${r.holder_name}</b>${r.is_sleek ? html`<span class="muted"> — Sleek</span>` : ''}</span>
      <span class="badge badge-light">${fmtPct(r.share_pct)}%</span>
      <button type="button" class="btn btn-sm btn-outline" @click=${() => onRemove(i)}>Remove</button>
    </div>`)}</div>`;

  const createCap = () => {
    const total = capTotal(capRows.value);
    return html`${capRows.value.length ? capEditor(capRows.value, removeHolder)
      : html`<div class="muted-block">No shareholders added.</div>`}
      <div class=${'form-note' + (capRows.value.length && total !== 100 ? ' text-bad' : '')}>Total: ${total}%${capRows.value.length && total !== 100 ? ' (must be 100)' : ''}</div>`;
  };

  const manageBody = () => {
    if (mLoading.value) return html`<div class="muted-block">Loading…</div>`;
    const d = detail.value; if (!d) return html`<div class="muted-block">—</div>`;
    const bz = mBusy.value, total = capTotal(mCap.value);
    return html`
      <div class="form-note">House company: <b>${d.house_company_name || ('#' + (d.house_company_id || '—'))}</b> · Country id ${d.country_id || '—'}</div>
      <h4 class="mt-2">Members</h4>
      <div class="row-list grants-list">
        ${(d.members || []).map(m => html`<div class="row-card">
          <span class="row-main"><b>${m.display_name || m.email}</b><span class="muted"> — ${roleLabel(m.role)}</span></span>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${bz === 'm' + m.user_id}
                  @click=${() => removeMember(m.user_id)}>${bz === 'm' + m.user_id ? '…' : 'Remove'}</button>
        </div>`)}
      </div>
      <h4 class="mt-2">Cap table</h4>
      ${mCap.value.length ? capEditor(mCap.value, removeCap) : html`<div class="muted-block">No shareholders.</div>`}
      <div class=${'form-note' + (total !== 100 ? ' text-bad' : '')}>Total: ${total}%${total !== 100 ? ' (must be 100 to save)' : ''}</div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">Partners</h1>
          <div class="page-sub">Per-country governing entities — own books + cap table</div>
        </div>
        <button class="btn btn-primary" @click=${openForm}>+ Add partner</button>
      </div>
      <div class="form-note">
        A partner governs one country: creating it auto-provisions its own books (a house company)
        and makes its owner govern every company in that country. Shareholders (the cap table) must
        sum to 100%.
      </div>
      ${list}

      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head"><h3>Add partner</h3>
            <button type="button" class="modal-x" @click=${closeForm}>✕</button></div>
          <label class="form-label">Partner name</label>
          <input class="form-control mb-2" .value=${fName} @input=${e => { fName.value = e.target.value; }}
                 placeholder="e.g. Sleek Finland">
          <label class="form-label">Country</label>
          <div class="mb-2">${countryPick.el}</div>
          <label class="form-label">Owner / super admin (optional)</label>
          <div class="mb-2">${ownerPick.el}</div>
          <label class="form-label">Shareholders (cap table)</label>
          ${createCap}
          <div class="de-item-wrap mt-1">
            <input class="form-control" .value=${hName} @input=${e => { hName.value = e.target.value; }}
                   placeholder="e.g. Sleek Holding">
            <input class="form-control" type="number" step="0.001" .value=${hPct}
                   @input=${e => { hPct.value = e.target.value; }} placeholder="e.g. 25">
            <label class="de-send-toggle"><input type="checkbox" .checked=${hSleek}
                   @change=${e => { hSleek.value = e.target.checked; }}> Sleek</label>
            <button type="button" class="btn btn-outline" @click=${addHolder}>Add</button>
          </div>
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 my-2">${formErr.value}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>
              ${() => saving.value ? 'Creating…' : 'Create partner'}</button>
          </div>
        </form>
      </div>

      <div class="modal-overlay" ?hidden=${() => !manageFor.value}>
        <div class="modal-card">
          <div class="modal-head"><h3>Manage${() => manageFor.value ? ' — ' + manageFor.value.name : ''}</h3>
            <button type="button" class="modal-x" @click=${closeManage}>✕</button></div>
          ${manageBody}
          <label class="form-label mt-2">Add member</label>
          <div class="mb-2">${memberPick.el}</div>
          <div class="de-item-wrap mb-2">
            <select class="form-control" .value=${mRole} @change=${e => { mRole.value = e.target.value; }}>
              ${MEMBER_ROLES.map(r => html`<option value=${r.value}>${r.label}</option>`)}
            </select>
            <button type="button" class="btn btn-outline" @click=${addMember}>Add member</button>
          </div>
          <label class="form-label">Add shareholder</label>
          <div class="de-item-wrap mb-2">
            <input class="form-control" .value=${mName} @input=${e => { mName.value = e.target.value; }}
                   placeholder="e.g. LA Investments">
            <input class="form-control" type="number" step="0.001" .value=${mPct}
                   @input=${e => { mPct.value = e.target.value; }} placeholder="e.g. 37.5">
            <label class="de-send-toggle"><input type="checkbox" .checked=${mSleek}
                   @change=${e => { mSleek.value = e.target.checked; }}> Sleek</label>
            <button type="button" class="btn btn-outline" @click=${addCap}>Add</button>
          </div>
          ${() => mErr.value ? html`<div class="alert alert-danger py-2 mb-2">${mErr.value}</div>` : null}
          <div class="form-actions">
            <button type="button" class="btn btn-outline" ?disabled=${() => mBusy.value === 'cap'}
                    @click=${saveCap}>${() => mBusy.value === 'cap' ? 'Saving…' : 'Save cap table'}</button>
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-primary" @click=${closeManage}>Done</button>
          </div>
        </div>
      </div>
    </div>`;

  return shell('partners', 'Partners', content);
}
